import React, { useEffect, useState } from 'react';
import { ArrowLeft, Mail, Map, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogPost } from '../types/blog';
import { blogPosts } from '../data/blogPosts';
import { BlogCard } from '../components/blog/BlogCard';
import { BlogPopup } from '../components/blog/BlogPopup';

function BlogPage() {
  const navigate = useNavigate();
  const { blogId } = useParams<{ blogId?: string }>();
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 12;

  // Handle URL-based blog selection
  useEffect(() => {
    if (blogId) {
      const post = blogPosts.find(p => p.id === blogId);
      if (post) {
        setSelectedPost(post);
      } else {
        // Blog not found, redirect to main blog page
        navigate('/blog', { replace: true });
      }
    } else {
      setSelectedPost(null);
    }
  }, [blogId, navigate]);

  // Handle cursor glow effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleCardClick = (post: BlogPost) => {
    navigate(`/blog/${post.id}`);
  };

  const handleClosePopup = () => {
    navigate('/blog');
  };

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  
  // Sort blog posts by publish date (newest first)
  const sortedBlogPosts = [...blogPosts].sort((a, b) => 
    new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );
  
  const currentPosts = sortedBlogPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(sortedBlogPosts.length / postsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      paginate(currentPage + 1);
    }
  };

  // Pagination component
  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 my-8">
        {/* Previous Button */}
        <button
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all duration-300 ${
            currentPage === 1
              ? 'text-gray-500 cursor-not-allowed'
              : 'text-white hover:text-green-400 hover:bg-green-400/10'
          }`}
        >
          <ChevronLeft size={16} />
          Previous
        </button>

        {/* First page if not visible */}
        {startPage > 1 && (
          <>
            <button
              onClick={() => paginate(1)}
              className="px-3 py-2 rounded-lg text-white hover:text-green-400 hover:bg-green-400/10 transition-all duration-300"
            >
              1
            </button>
            {startPage > 2 && (
              <span className="text-gray-500 px-2">...</span>
            )}
          </>
        )}

        {/* Page Numbers */}
        {pageNumbers.map((number) => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`px-3 py-2 rounded-lg transition-all duration-300 ${
              currentPage === number
                ? 'bg-green-400 text-black font-semibold'
                : 'text-white hover:text-green-400 hover:bg-green-400/10'
            }`}
          >
            {number}
          </button>
        ))}

        {/* Last page if not visible */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="text-gray-500 px-2">...</span>
            )}
            <button
              onClick={() => paginate(totalPages)}
              className="px-3 py-2 rounded-lg text-white hover:text-green-400 hover:bg-green-400/10 transition-all duration-300"
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next Button */}
        <button
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all duration-300 ${
            currentPage === totalPages
              ? 'text-gray-500 cursor-not-allowed'
              : 'text-white hover:text-green-400 hover:bg-green-400/10'
          }`}
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  // SEO meta tags
  const pageTitle = selectedPost 
    ? `${selectedPost.title} | Blog | Escaping The Matrix`
    : 'Blog | Escaping The Matrix';

  const pageDescription = selectedPost
    ? selectedPost.hook
    : 'Discover insights on AI automation, digital solutions, and escaping the matrix of outdated technology.';

  const pageImage = selectedPost?.image || 'https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=1200&h=630';

  return (
    <div className="relative min-h-screen bg-black text-white overflow-visible">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:url" content={`https://escapingthematrix.io/blog${selectedPost ? `/${selectedPost.id}` : ''}`} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={pageImage} />
        <meta property="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <div
        className="cursor-glow"
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
        }}
      />
      
      <div className="relative z-10">
        <nav className="fixed top-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm z-50">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-green-400 transition-colors duration-300"
          >
            <ArrowLeft size={20} />
            Back to Main Page
          </button>
        </nav>

        <main className="container mx-auto px-4 md:px-6 pt-24 pb-12">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wider text-green-400 mb-4">
                BLOG
              </h1>
              <p className="text-lg text-gray-300">
                Insights on AI automation, digital solutions.
              </p>
              {totalPages > 1 && (
                <p className="text-sm text-gray-400 mt-2">
                  Showing {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, sortedBlogPosts.length)} of {sortedBlogPosts.length} posts
                </p>
              )}
            </div>

            {/* Top Pagination Controls */}
            <PaginationControls />

            {/* Blog Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
              {currentPosts.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  onClick={() => handleCardClick(post)}
                />
              ))}
            </div>

            {/* Bottom Pagination Controls */}
            <PaginationControls />

            {/* Footer Info */}
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                Want to stay updated? Follow us on our social channels for the latest insights.
              </p>
            </div>
          </div>
        </main>

        {/* Blog Popup */}
        {selectedPost && (
          <BlogPopup
            post={selectedPost}
            onClose={handleClosePopup}
          />
        )}

        <footer className="fixed bottom-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="flex justify-between">
            <div className="flex space-x-6">
              <a href="https://x.com/EscapeMatrixAI" target="_blank" rel="noopener noreferrer" className="nav-link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-current">
                  <path d="M16.99 0H20.298L13.071 8.26L21.573 19.5H14.916L9.702 12.683L3.736 19.5H0.426L8.156 10.665L0 0H6.826L11.539 6.231L16.99 0ZM15.829 17.52H17.662L5.83 1.876H3.863L15.829 17.52Z" fill="currentColor"/>
                </svg>
              </a>
              <a href="https://t.me/AIEscapingtheMatrix" target="_blank" rel="noopener noreferrer" className="nav-link">
                <FaTelegram size={18} />
              </a>
              <a href="mailto:EscapingtheMatrixAI@proton.me" className="nav-link">
                <Mail size={18} />
              </a>
              <a href="https://sitemap.escapingthematrix.io/" target="_blank" rel="noopener noreferrer" className="nav-link">
                <Map size={18} />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default BlogPage;